import { useState } from 'react';
import { DataTableState } from 'src/shared/components/data-table/dataTableTypes';
import { useAppSelector } from 'src/store';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAuthService from 'src/shared/hooks/useAuthService';
import { getUserManagementListFilterState } from 'src/app/admin/views/user-management/state/userManagementSelector';
import {
    useGetCustomerUsersForSuperAdminQuery,
    useGetCustomerUsersQuery,
} from 'src/app/admin/views/user-management/state/api/userManagementGraphSlice';
import { checkIfCustomerAdmin } from 'src/app/admin/views/user-management/userManagementHelpers';
import { User, UserType } from 'src/shared/types';
export const USER_MANAGEMENT_LIST_ROWS_PER_PAGE = 10;

const useUserManagementList = () => {
    const { currentCustomerId } = useCustomer();
    const { isCurrentUserOnboardingManager, isCurrentUserSuperAdmin } = useAuthService();
    const filters = useAppSelector(getUserManagementListFilterState);
    const [tableState, setTableState] = useState<Partial<DataTableState>>({
        fixedHeader: true,
        hiddenColumnIds: new Set(['userId']),
        rowsPerPage: USER_MANAGEMENT_LIST_ROWS_PER_PAGE,
    });

    const emailQuery = filters.userEmailSearchText?.trim() || undefined;

    const { isLoading: isFetchingUsersForSuperAdmin, currentData: currentDataForSuperAdmin } = useGetCustomerUsersForSuperAdminQuery(
        {
            filters: {
                customerId: currentCustomerId,
                ...(emailQuery ? { emailQuery } : {}),
                userType: isCurrentUserOnboardingManager ? UserType.EXTERNAL : null,
            },
            pagination: {
                page: filters.currentPageNumber || 1,
                pageSize: tableState.rowsPerPage as number,
            },
        },
        {
            skip: !(isCurrentUserOnboardingManager || isCurrentUserSuperAdmin),
            refetchOnMountOrArgChange: true,
        }
    );

    const {
        isLoading: isLoadingCustomerUsers,
        isFetching: isFetchingCustomerUsers,
        currentData: currentDataForCustomerAdmin,
    } = useGetCustomerUsersQuery(
        {
            filters: {
                customerId: currentCustomerId,
                ...(emailQuery ? { emailQuery } : {}),
            },
            pagination: {
                page: filters.currentPageNumber || 1,
                pageSize: tableState.rowsPerPage as number,
            },
        },
        {
            skip: isCurrentUserOnboardingManager || isCurrentUserSuperAdmin,
            refetchOnMountOrArgChange: true,
        }
    );

    const getSortedUsers = (users: User[]) => {
        return [...users].sort((val1, val2) => {
            const isVal1CustomerAdmin = checkIfCustomerAdmin(val1.customers, currentCustomerId);
            const isVal2CustomerAdmin = checkIfCustomerAdmin(val2.customers, currentCustomerId);
            if (isVal1CustomerAdmin && !isVal2CustomerAdmin) {
                return -1;
            } else if (!isVal1CustomerAdmin && isVal2CustomerAdmin) {
                return 1;
            } else {
                return val1.id - val2.id;
            }
        });
    };

    return {
        isLoadingCustomerUsers,
        isFetchingCustomerUsers,
        currentDataForCustomerAdmin,
        sortedUsersForCustomerAdmin: getSortedUsers(currentDataForCustomerAdmin?.users || []),
        isFetchingUsersForSuperAdmin,
        currentDataForSuperAdmin,
        sortedUsersForSuperAdmin: getSortedUsers(currentDataForSuperAdmin?.users || []),
        tableState,
        setTableState,
    };
};

export default useUserManagementList;
